import React, { useEffect } from "react";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import NotFound from "./components/NotFound";
import { PublicRoutes, PrivateRoutes } from "./routes/LazyRoutes";
import { useSelector, useDispatch } from "react-redux";
import { PrivateRoute } from "./middlewares/PrivateRoute";
import { setAutoValue } from "./Redux/themeSlice";
import { useTheme } from "@mui/material";
import "./App.css";
import { PageTitleProvider } from "./middlewares/PageTitleContext";
import useSocketUserNotifications from "./hooks/useSocketUserNotifications";
import NotificationToast from "./components/pushMessages/NotificationToast";
import { toast } from "react-toastify";
import axiosInstance from "./axios/axiosInstance";
import { sendNotificationRoute, messagesUnreadCountRoute } from "./routes/apiRoutes";
import { useNavigate } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import queryClient from "./config/query-client";
import AppTour from "./components/onboarding/AppTour";
import { canFetchMessagesUnreadCount } from "./utils/managementPermissions";
function App() {
  ///////////////////////////////////////////////////////////////////////////////////
  //code to get background notification messages from service worker to react app
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const mode = useSelector((state) => state.themeValues.originalMode);
  const currentUser = useSelector((state) => state.authenticationValues.currentUser);
  const userId = currentUser?.id;
  // Subscribe only when logged in and we have an id
useSocketUserNotifications(
  userId,
  {
    // New notification received
    'new-notification': (payload) => {
      const msg = payload?.data?.notification || payload?.notification;
      const patientId = payload?.data?.patient_id || payload?.patient_id;
      const navigateTo = (msg?.type === "case" && patientId)  ? `/patients/${patientId}` : (patientId || msg?.task?.patient_id) ? `/patients/${patientId}` : '/tasks';


      // Show real-time toast for instant feedback
      showPushToast({ title: msg?.title, body: msg?.body, iconUrl: msg?.iconUrl, navigateTo });

      // Immediately reconcile with server to avoid client-only drift
      const requests = [
        axiosInstance.get(`${sendNotificationRoute}?limit=10&&page=1&&id=${currentUser.id}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        }),
      ];
      if (canFetchMessagesUnreadCount(currentUser)) {
        requests.push(
          axiosInstance.get(messagesUnreadCountRoute, {
            headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
          })
        );
      }
      Promise.all(requests)
        .then(([response, unreadRes]) => {
          const list = response?.data?.data?.notifications || [];
          const totalUnread = response?.data?.data?.unreadCount;
          const mapped = list.map((notification) => ({
            body: notification?.body,
            title: notification?.title,
            notificationId: notification?.id,
            isRead: notification?.is_read || notification.isRead,
            date: notification.createdAt,
            msgType: notification?.type,
            patientId:
              notification?.patient_id ||
              notification?.task?.patient_id ||
              notification?.case?.patient_id ||
              null,
            key: `${notification?.title}-${notification?.body}`,
            ...(notification?.type &&
              notification?.id && {
                type: notification?.type,
                id: notification?.id,
              }),
          }));
          const messagesUnreadCount =
            unreadRes?.data?.data?.unreadCount ?? 0;
          dispatch({
            type: "notificationValues/receiveAll",
            payload: {
              messages: mapped,
              unreadCount: totalUnread,
              messagesUnreadCount,
            },
          });
        })
        .catch(() => {});
    },
  },
  [userId]
);

  // code to get changes in os color mode
  // TODO: Add this Later
  // useEffect(() => {
  //   const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

  //   const handleChange = (e) => {
  //     if (e.matches) {
  //       dispatch(setAutoValue({ autoValue: "dark" }));
  //     } else {
  //       dispatch(setAutoValue({ autoValue: "light" }));
  //     }
  //   };

  //   if (mediaQuery.matches) {
  //     dispatch(setAutoValue({ autoValue: "dark" }));
  //   } else {
  //     dispatch(setAutoValue({ autoValue: "light" }));
  //   }

  //   if (mediaQuery.addEventListener) {
  //     mediaQuery.addEventListener("change", handleChange);
  //   } else if (mediaQuery.addListener) {
  //     mediaQuery.addListener(handleChange);
  //   }
  //   return () => {
  //     if (mediaQuery.removeEventListener) {
  //       mediaQuery.removeEventListener("change", handleChange);
  //     } else if (mediaQuery.removeListener) {
  //       mediaQuery.removeListener(handleChange);
  //     }
  //   };
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, []);
  /////////////////////////////////////////////////////////////////////////////////////
  function showPushToast({ title, body, iconUrl, navigateTo }) {

    const toastId = toast(({ closeToast }) => (
      <NotificationToast
        title={title}
        body={body}
        iconUrl={iconUrl}
        timeText={"Just now"}
        dir={theme.direction}
        onOpen={() => {
          closeToast?.();
          if (navigateTo) navigate(navigateTo);
        }}
        onDismiss={() => closeToast?.()}
      />
    ), {
      icon: false,                 
      closeButton: false,          
      autoClose: 6000,             
      pauseOnHover: true,
      hideProgressBar: true,       
      draggable: false,
      closeOnClick: false,         
      position: theme.direction === "rtl" ? "top-left" : "top-right",
      className: theme.direction === "rtl" ? "toast-container-rtl" : "",
    });
  
    return toastId;
  }

  return (
    <div>
    <QueryClientProvider client={queryClient}>
      <ToastContainer
        theme={`${mode}`}
        style={{
          direction: theme.direction,
          textAlign: theme.direction === "rtl" ? "right" : "left",
        }}
        toastClassName={theme.direction === "rtl" ? "toast-rtl" : ""}
        bodyClassName={theme.direction === "rtl" ? "toast-body-rtl" : ""}
        className={theme.direction === "rtl" ? "toast-container-rtl" : ""}
        position={theme.direction === "rtl" ? "top-left" : "top-right"}
        limit={3}
        progressStyle={{}}
      />
      <AppTour />
      {/* <BrowserRouter> */}
        <PageTitleProvider>
        <Routes>
          {PublicRoutes.map((route) => (
            <Route key={route.path} path={route.path} element={route.element} />
          ))}
          {PrivateRoutes.map((route) => (
            <Route
              key={route.path}
              path={route.path}
              element={<PrivateRoute children={route.element} />}
            />
          ))}
          <Route
            path="*"
            element={<NotFound title="Page not found - Fixafib Connect" />}
            />
          </Routes>
        </PageTitleProvider>
      {/* </BrowserRouter> */}
      </QueryClientProvider>
    </div>
  );
}

export default App;
